// MilestoneActiveFilterSummary — single-line summary strip rendered
// above the milestone chip bar whenever any filter or search is active.
// Mirrors the deliverable ActiveFilterSummary pattern so both tabs
// share the same Clear-all surface (top-line strip with summary text +
// link, instead of an inline pill on the chip row).
//
// Renders only when at least one filter or a non-empty search is
// active — when nothing is narrowed, the strip is omitted entirely so
// the empty/default state stays visually quiet.

import type { Sow } from "../../../../types";
import type { MilestoneDerivedStatus } from "../../../../utils/deriveMilestoneStatus";
import {
  UNATTACHED_TOKEN,
  type MilestoneFilterChipBarFilters,
} from "./milestoneUrlState";

export interface MilestoneActiveFilterSummaryProps {
  filters: MilestoneFilterChipBarFilters;
  search: string;
  onClearAll: () => void;
  /** SOWs on the parent contract — used to render human-readable labels
   *  for active SOW chips. Optional: when omitted, ids show raw. */
  sows?: readonly Sow[];
}

const STATUS_LABELS: Record<MilestoneDerivedStatus, string> = {
  upcoming: "Upcoming",
  delivered: "Delivered",
  "partially-accepted": "Partially accepted",
  accepted: "Accepted",
  paid: "Paid",
};

function joinStatuses(values: MilestoneDerivedStatus[]): string {
  return values.map((v) => STATUS_LABELS[v]).join(", ");
}

function joinSows(
  selected: readonly string[],
  sows: readonly Sow[] | undefined,
): string {
  return selected
    .map((token) => {
      if (token === UNATTACHED_TOKEN) return "Unattached";
      const s = sows?.find((x) => x.id === token);
      return s ? s.number : token;
    })
    .join(", ");
}

export default function MilestoneActiveFilterSummary({
  filters,
  search,
  onClearAll,
  sows,
}: MilestoneActiveFilterSummaryProps) {
  const trimmedSearch = search.trim();
  const anyActive =
    filters.status.length > 0 ||
    filters.awaitingAcceptance ||
    filters.sow.length > 0 ||
    filters.includeArchived ||
    trimmedSearch.length > 0;

  if (!anyActive) return null;

  const parts: string[] = [];
  if (filters.status.length > 0) {
    parts.push(`Status: ${joinStatuses(filters.status)}`);
  }
  if (filters.awaitingAcceptance) parts.push("Awaiting acceptance");
  if (filters.sow.length > 0) parts.push(`SOW: ${joinSows(filters.sow, sows)}`);
  if (filters.includeArchived) parts.push("Include archived");
  if (trimmedSearch) parts.push(`Search: '${trimmedSearch}'`);

  return (
    <div
      data-testid="milestone-active-filter-summary"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300"
    >
      <span>{parts.join(" · ")}</span>
      <button
        type="button"
        onClick={onClearAll}
        className="text-caption font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
      >
        Clear all
      </button>
    </div>
  );
}
